import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useFetch } from "@/hooks/useFetch";
import { media } from "@/lib/api";
import { dateTime, money, titleCase } from "@/lib/format";
import { DemoBadge, ErrorState, KeyValue, PageHeader, SectionCard, StatusBadge, Thumb } from "@/components/admin/AdminUI";

export default function AdminCustomerDetail() {
  const { cid } = useParams();
  const { data: c, loading, error, reload } = useFetch(`/admin/customers/${cid}`);
  useEffect(() => { document.title = `${c ? `${c.first_name} ${c.last_name}` : "Client"} · Admin`; }, [c]);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading && !c) return <div className="space-y-6"><Skeleton className="h-12 w-72" /><Skeleton className="h-64 w-full" /></div>;

  const consents = c.consents || {};

  return (
    <div data-testid="admin-customer-detail-page">
      <PageHeader
        eyebrow="Customers · Client 360"
        title={`${c.first_name} ${c.last_name}`}
        backTo="/admin/customers"
        backLabel="Client directory"
        description={<span className="inline-flex flex-wrap items-center gap-2"><StatusBadge status={c.status} />{c.is_demo && <DemoBadge />}<span className="text-xs text-muted-foreground">{c.email} · client since {dateTime(c.created_at)}</span></span>}
      />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <SectionCard title="Profile" testId="customer-profile-card">
            <KeyValue items={[["Email", c.email], ["Phone", c.phone], ["Country", c.country_code], ["Preferred currency", c.preferred_currency], ["Email verified", c.email_verified ? "Yes" : "No"], ["Source", c.source], ["Last sign in", c.last_login_at ? dateTime(c.last_login_at) : "Never"], ["Guest", c.is_guest ? "Yes" : "No"]]} />
          </SectionCard>
          <SectionCard title="Marketing & privacy consents" description="Latest recorded decision per purpose." testId="customer-consents-card">
            <ul className="space-y-2.5">
              {Object.entries(consents).map(([purpose, v]) => (
                <li key={purpose} className="flex items-center justify-between gap-3 text-sm"><span>{titleCase(purpose)}<span className="block text-[11px] text-muted-foreground">{v.recorded_at ? `${dateTime(v.recorded_at)} · ${v.source || ""}` : "Never recorded"}</span></span><StatusBadge status={v.granted ? "granted" : "declined"} label={v.granted ? "Granted" : "Declined"} tone={v.granted ? "good" : "muted"} /></li>
              ))}
              {!Object.keys(consents).length && <li className="text-sm text-muted-foreground">No consent purposes configured.</li>}
            </ul>
          </SectionCard>
        </div>
        <div className="lg:col-span-2 space-y-6">
          <SectionCard title="Addresses" testId="customer-addresses-card">
            {!c.addresses?.length ? <p className="text-sm text-muted-foreground">No addresses saved.</p> : (
              <ul className="grid sm:grid-cols-2 gap-3">
                {c.addresses.map((a) => (
                  <li key={a.id} className="rounded-lg border p-4 text-sm" data-testid="customer-address">
                    <p className="font-medium flex items-center gap-2">{a.label || titleCase(a.type || "Address")}{a.is_default && <StatusBadge status="default" label="Default" tone="ink" />}</p>
                    <p className="text-muted-foreground mt-1 leading-relaxed">{[a.first_name && `${a.first_name} ${a.last_name || ""}`, a.line1, a.line2, [a.postal_code, a.city].filter(Boolean).join(" "), a.region, a.country_code].filter(Boolean).map((s, i) => <span key={i} className="block">{s}</span>)}</p>
                    {a.phone && <p className="text-xs text-muted-foreground mt-1">{a.phone}</p>}
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
          <div className="grid md:grid-cols-2 gap-6">
            <SectionCard title={`Wishlist · ${c.wishlist?.count || 0}`} testId="customer-wishlist-card">
              {!c.wishlist?.items?.length ? <p className="text-sm text-muted-foreground">Nothing saved yet.</p> : (
                <ul className="space-y-3">
                  {c.wishlist.items.map((w) => (
                    <li key={w.product_id} className="flex items-center gap-3 text-sm"><Thumb src={media(w.product?.primary_image, 120)} alt="" /><div className="min-w-0"><Link to={`/admin/products/${w.product_id}`} className="font-medium hover:underline underline-offset-4 truncate block">{w.product?.name}</Link><p className="text-xs text-muted-foreground">{w.product?.brand_name} · {w.available ? money(w.product?.min_price) : "No longer available"}</p></div></li>
                  ))}
                </ul>
              )}
            </SectionCard>
            <SectionCard title={`Bag · ${c.cart?.item_count || 0} item${c.cart?.item_count === 1 ? "" : "s"}`} description={c.cart?.subtotal ? `Estimated subtotal ${money(c.cart.subtotal)}` : undefined} testId="customer-cart-card">
              {!c.cart?.items?.length ? <p className="text-sm text-muted-foreground">Bag is empty.</p> : (
                <ul className="space-y-3">
                  {c.cart.items.map((l) => (
                    <li key={l.id} className="flex items-center gap-3 text-sm"><Thumb src={media(l.product?.primary_image, 120)} alt="" /><div className="min-w-0 flex-1"><p className="font-medium truncate">{l.product?.name}</p><p className="text-xs text-muted-foreground">{l.sku} · {l.color} / {l.size} · ×{l.quantity}</p></div><span className="tabular text-sm">{money(l.unit_price)}</span></li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>
          <SectionCard title="Orders" description="Checkout and the order engine arrive in Phase 4." testId="customer-orders-card">
            {!c.orders?.length ? <p className="text-sm text-muted-foreground">No orders yet.</p> : <ul className="text-sm">{c.orders.map((o) => <li key={o.id}>{o.order_number || o.id} · {money(o.total)} · <StatusBadge status={o.status} /></li>)}</ul>}
          </SectionCard>
          {c.consent_history?.length > 0 && (
            <SectionCard title="Consent history" testId="customer-consent-history">
              <ul className="text-xs space-y-1.5 text-muted-foreground">{c.consent_history.slice(0, 20).map((h) => <li key={h.id || `${h.purpose}-${h.recorded_at}`}>{dateTime(h.recorded_at)} · {titleCase(h.purpose)} → <span className={h.granted ? "text-[hsl(var(--accent-forest))]" : ""}>{h.granted ? "granted" : "declined"}</span> ({h.source})</li>)}</ul>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}
